import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TrustBar from '../../components/feature/TrustBar';
import PageHero from '../../components/feature/PageHero';
import ServicesOverview from '../../components/feature/ServicesOverview';
import AIAnswerBlock from '../../components/feature/AIAnswerBlock';
import ProcessSection from '../../components/feature/ProcessSection';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import ServiceAreas from '../../components/feature/ServiceAreas';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTA from '../../components/feature/FinalCTA';
import { siteConfig, services, testimonials, process, whyUsPoints, serviceRegions, homepageFaqs, homeAIAnswers } from '../../mocks/siteData';

const HERO_IMAGE = 'https://public.readdy.ai/ai/img_res/bdb9ccd3d46125f2e9b0de5b9e1e363f.jpg';

const PORTFOLIO = [
  { image: 'https://readdy.ai/api/search-image?query=international-auto-show-luxury-brand-exhibit-floor-display-vehicles-on-elevated-platforms-professional-event-staging-dramatic-spotlight-lighting-dark-atmosphere-editorial-automotive-2025&width=600&height=400&seq=pf1&orientation=landscape', label: 'National Auto Show', location: 'Las Vegas, NV' },
  { image: 'https://readdy.ai/api/search-image?query=new-car-model-press-launch-event-vehicle-reveal-on-dramatic-lit-stage-automotive-manufacturer-event-dark-cinematic-professional-photography-spotlit-stage-editorial-2025&width=600&height=400&seq=pf2&orientation=landscape', label: 'OEM Press Launch', location: 'Los Angeles, CA' },
  { image: 'https://readdy.ai/api/search-image?query=fleet-of-luxury-vehicles-lined-up-at-premium-event-venue-pristine-showroom-quality-corporate-automotive-brand-program-golden-dusk-lighting-professional-wide-angle-editorial-2025&width=600&height=400&seq=pf3&orientation=landscape', label: 'Manufacturer Tour', location: 'Chicago, IL' },
  { image: 'https://readdy.ai/api/search-image?query=luxury-sports-car-display-at-premium-golf-tournament-sponsorship-activation-branded-automotive-platform-on-manicured-green-professional-event-setup-cinematic-afternoon-2025&width=600&height=400&seq=pf4&orientation=landscape', label: 'PGA Tour Activation', location: 'Scottsdale, AZ' },
];

function MidPageCTA() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.2 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative py-20 px-6 lg:px-12 xl:px-16 overflow-hidden"
      style={{
        background: `linear-gradient(to right, rgba(0,0,0,0.92) 50%, rgba(0,0,0,0.6) 100%), url(https://readdy.ai/api/search-image?query=luxury-automotive-vehicles-staged-at-dramatic-event-space-spotlights-dark-professional-presentation-cinematic-side-angle-2025&width=1600&height=600&seq=mid-cta-1&orientation=landscape) center/cover no-repeat`,
      }}
    >
      <div className={`max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <div>
          <div className="text-gold text-[10px] font-sans tracking-[0.35em] uppercase font-semibold mb-3">Nationwide Events</div>
          <h2 className="font-serif text-white text-3xl lg:text-4xl font-bold leading-tight max-w-xl">
            Planning a Launch, Tour, or Auto Show Program?
          </h2>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <Link
            to="/services"
            className="bg-gold hover:bg-gold-light text-[#0A0A0A] font-sans font-semibold text-sm px-8 py-4 uppercase tracking-widest transition-colors cursor-pointer whitespace-nowrap"
          >
            Plan Your Program
          </Link>
          <a
            href={siteConfig.phoneHref}
            className="border border-white/30 hover:border-white/60 text-white/70 hover:text-white font-sans font-medium text-sm px-8 py-4 uppercase tracking-widest transition-colors cursor-pointer whitespace-nowrap"
          >
            Talk to OTT
          </a>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      <Navbar />

      {/* 1. Hero */}
      <PageHero
        eyebrow="Nationwide Automotive Event Services"
        headline="The Operational Partner Behind Flawless Vehicle Presentation"
        subheadline="OTT Car Care delivers end-to-end automotive event management, fleet detailing, press launch support, and nationwide program execution for OEMs, agencies, and dealer groups across all 50 states."
        primaryCTA={{ label: 'Request Event Support', href: '/services' }}
        secondaryCTA={{ label: 'See Our Services', href: '/services' }}
        backgroundImage={HERO_IMAGE}
        badge="Serving All 50 States"
      />

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Services Overview */}
      <ServicesOverview
        title="Event & Program Services"
        subtitle="From press launches and auto shows to manufacturer tours and golf activations — OTT delivers white-glove execution at any scale."
        services={services}
        ctaLabel="Explore All Services"
        ctaHref="/services"
      />

      {/* 4. AI Answer Block */}
      <AIAnswerBlock
        answers={homeAIAnswers}
        title="What Is OTT Car Care?"
        subtitle="Clear answers about who we are, who we serve, and why the top automotive brands trust OTT."
      />

      {/* 5. Process */}
      <ProcessSection
        steps={process}
        title="A Refined, Proven Process"
        subtitle="From first consultation to final debrief — our operational framework is built for programs that cannot fail."
      />

      {/* Mid-Page CTA */}
      <MidPageCTA />

      {/* 6. Why Choose OTT */}
      <WhyChooseUs
        points={whyUsPoints}
        title="Built for Programs That Cannot Fail"
        subtitle="What separates OTT from every other detailing company: we operate at an event-agency level with national infrastructure and zero-compromise standards."
      />

      {/* 7. Nationwide Coverage */}
      <ServiceAreas
        regions={serviceRegions}
        title="Nationwide Coverage"
        subtitle="HQ in Lake Forest, CA — deployed everywhere. OTT operates across all 50 states with a national crew network built for programs of any scale."
        showNational
      />

      {/* 8. Testimonials + Portfolio */}
      <TestimonialsSection
        testimonials={testimonials}
        title="Client Proof & Portfolio"
        subtitle="Trusted by automotive brands, event agencies, and dealer groups who demand results at every scale."
        portfolio={PORTFOLIO}
      />

      {/* 9. FAQ */}
      <FAQSection
        faqs={homepageFaqs}
        title="Common Questions"
        subtitle="Everything you need to know before starting your program with OTT."
      />

      {/* 10. Final CTA / Lead Form */}
      <FinalCTA
        headline="Ready to Elevate Your Program?"
        subheadline="Tell us about your event or fleet program. OTT deploys nationwide — no program too large, no market too far. Let's build something exceptional."
      />

      <Footer />
    </div>
  );
}
